import {
  createContext,
  useCallback,
  useContext,
  useRef,
  type ReactNode,
} from "react";
import { Accordion as BaseAccordion } from "@base-ui/react/accordion";
import { IconChevronDown } from "@tabler/icons-react";
import styles from "./collapsibleStyles.module.css";
import type { CollapsibleVariant } from "./CollapsibleCard";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import Text from "./Text";

type AccordionItemValue = string | number;

/** Like CollapsibleVariant, plus "filled" — an info-tinted section with an
 * inset white content card (used for grouped form sections). */
type AccordionItemVariant = CollapsibleVariant | "filled";

/** How consecutive items are separated: "gap" spaces them out (default),
 * "border" sits them flush with a divider between each. */
type AccordionDivider = "gap" | "border";

const ROOT_DIVIDER_CLASS: Record<AccordionDivider, string> = {
  gap: styles.accordionRootGap,
  border: styles.accordionRootBordered,
};

function getScrollParent(el: HTMLElement): HTMLElement {
  let node = el.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (
      /(auto|scroll|overlay)/.test(overflowY) &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return (document.scrollingElement as HTMLElement) ?? document.documentElement;
}

/**
 * Counter-scrolls so `el` stays at viewport position `targetTop` while the
 * previously open section above it animates closed — otherwise the just-opened
 * section would jump up under the cursor. Corrects every frame until the
 * collapse transition settles.
 */
function keepElementInPlace(el: HTMLElement, targetTop: number) {
  const scrollParent = getScrollParent(el);
  let frames = 0;
  let settled = 0;
  const correct = () => {
    const delta = el.getBoundingClientRect().top - targetTop;
    if (Math.abs(delta) > 0.5) {
      scrollParent.scrollTop += delta;
      settled = 0;
    } else {
      settled += 1;
    }
    frames += 1;
    if (settled < 3 && frames < 60) requestAnimationFrame(correct);
  };
  requestAnimationFrame(correct);
}

/** Lets each AccordionItem register its DOM node so the Accordion can anchor
 * the scroll position to the item being opened. */
const AccordionItemRegistry = createContext<
  ((value: AccordionItemValue, el: HTMLElement | null) => void) | null
>(null);

interface AccordionProps {
  children: ReactNode;
  /** Controlled list of expanded item values. */
  value?: AccordionItemValue[];
  /** Initially expanded item values (uncontrolled). */
  defaultValue?: AccordionItemValue[];
  onValueChange?: (value: AccordionItemValue[]) => void;
  /** Allow several panels open at once. Defaults to single-open. */
  multiple?: boolean;
  disabled?: boolean;
  /** How consecutive items are separated. Defaults to "gap". */
  divider?: AccordionDivider;
  /** Keep the just-opened section under the cursor when a section above it
   * collapses. Only useful for single-open accordions; items need a `value`. */
  anchorOnOpen?: boolean;
  className?: string;
}

export default function Accordion({
  children,
  value,
  defaultValue,
  onValueChange,
  multiple = false,
  disabled,
  divider = "gap",
  anchorOnOpen = false,
  className,
}: AccordionProps) {
  const itemEls = useRef(new Map<AccordionItemValue, HTMLElement>());
  const openValues = useRef<AccordionItemValue[]>(value ?? defaultValue ?? []);

  const register = useCallback(
    (val: AccordionItemValue, el: HTMLElement | null) => {
      if (el) itemEls.current.set(val, el);
      else itemEls.current.delete(val);
    },
    [],
  );

  function handleValueChange(next: AccordionItemValue[]) {
    if (anchorOnOpen) {
      const before = new Set(openValues.current);
      const opened = next.find((v) => !before.has(v));
      const el = opened != null ? itemEls.current.get(opened) : undefined;
      // Capture the position before React re-renders, then pin it there.
      if (el) keepElementInPlace(el, el.getBoundingClientRect().top);
    }
    openValues.current = next;
    onValueChange?.(next);
  }

  return (
    <AccordionItemRegistry.Provider value={register}>
      <BaseAccordion.Root
        value={value}
        defaultValue={defaultValue}
        onValueChange={handleValueChange}
        multiple={multiple}
        disabled={disabled}
        className={classnames(
          styles.accordionRoot,
          ROOT_DIVIDER_CLASS[divider],
          className,
        )}
      >
        {children}
      </BaseAccordion.Root>
    </AccordionItemRegistry.Provider>
  );
}

interface AccordionItemProps {
  title: ReactNode;
  headerActions?: ReactNode;
  children?: ReactNode;
  /** Stable identifier; auto-generated by Base UI when omitted. */
  value?: AccordionItemValue;
  disabled?: boolean;
  /** "card" (default) is the bordered blue-header look; "plain" is lighter;
   * "filled" is an info-tinted section with an inset white content card. */
  variant?: AccordionItemVariant;
  className?: string;
}

const ITEM_CLASS: Record<AccordionItemVariant, string> = {
  card: styles.itemCard,
  plain: styles.itemPlain,
  filled: styles.itemFilled,
};

const TRIGGER_CLASS: Record<AccordionItemVariant, string | false> = {
  card: false,
  plain: styles.triggerPlain,
  filled: styles.triggerFilled,
};

const PANEL_CLASS: Record<AccordionItemVariant, string> = {
  card: styles.panelCard,
  plain: styles.panelPlain,
  filled: styles.panelFilled,
};

export function AccordionItem({
  title,
  headerActions,
  children,
  value,
  disabled,
  variant = "card",
  className,
}: AccordionItemProps) {
  const isCard = variant === "card";
  const register = useContext(AccordionItemRegistry);

  const itemRef = useCallback(
    (el: HTMLElement | null) => {
      if (value != null) register?.(value, el);
    },
    [register, value],
  );

  return (
    <BaseAccordion.Item
      ref={itemRef}
      value={value}
      disabled={disabled}
      className={classnames(ITEM_CLASS[variant], className)}
    >
      <BaseAccordion.Header
        className={classnames(styles.header, isCard && styles.headerCard)}
      >
        <BaseAccordion.Trigger
          className={classnames(styles.trigger, TRIGGER_CLASS[variant])}
        >
          <IconChevronDown
            size={18}
            className={classnames(styles.chevron, !isCard && styles.chevronEnd)}
          />
          <Text
            as="div"
            weight="bold"
            className={classnames(styles.title, !isCard && styles.titleGrow)}
          >
            {title}
          </Text>
        </BaseAccordion.Trigger>
        {headerActions && <div className={styles.actions}>{headerActions}</div>}
      </BaseAccordion.Header>
      {children && (
        <BaseAccordion.Panel
          className={classnames(styles.panel, PANEL_CLASS[variant])}
        >
          <div>{children}</div>
        </BaseAccordion.Panel>
      )}
    </BaseAccordion.Item>
  );
}
